import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  positionOpenerAxiosApi,
  predictionManagerAxiosApi
} from 'src/Config/axiosApi';
import {
  getOpenerState,
  startOpener,
  stopOpener,
  OpenerState
} from 'src/Api/exchanges';
import {
  EntryRule,
  EntryRuleType,
  EntryExecution,
  CreateEntryRuleDto,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  EntryOrdersResponse,
  MakerFlag
} from '../types/entry.types';

/**
 * Fetch all entry rules, optionally filtered by `ruleType` ('tna' or
 * 'sum_of_sides'). Mirrors `useUnwindingRules` so the page-level slicing
 * patterns (active vs paused, by pairId) carry over unchanged.
 */
export function useEntryRules(opts?: { ruleType?: EntryRuleType }) {
  const ruleType = opts?.ruleType;
  return useQuery<EntryRule[]>(
    ['entry-rules', ruleType ?? 'all'],
    async () => {
      const { data } = await positionOpenerAxiosApi.get<EntryRule[]>(
        '/rules',
        ruleType ? { params: { ruleType } } : undefined
      );
      return data;
    },
    { refetchInterval: 5000, keepPreviousData: true }
  );
}

export function useCreateEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateEntryRuleDto) => {
      const { data } = await positionOpenerAxiosApi.post<EntryRule>(
        '/rules',
        dto
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useUpdateEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateEntryRuleDto }) => {
      const { data } = await positionOpenerAxiosApi.patch<EntryRule>(
        `/rules/${id}`,
        dto
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useDeleteEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await positionOpenerAxiosApi.delete(`/rules/${id}`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useTogglePlatform() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      platform,
      enabled
    }: {
      id: string;
      platform: 'kalshi' | 'polymarket';
      enabled: boolean;
    }) => {
      const { data } = await positionOpenerAxiosApi.post(
        `/rules/${id}/toggle-platform`,
        { platform, enabled }
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Set one of the four per-(platform, side) maker flags on a rule.
 * Backend's RulesService.update() re-resolves the rule's path and
 * derives kalshiConfig.side / polymarketConfig.side from the merged
 * flag state — the caller only needs to pass the flag it wants to
 * change. The `clearOthers` option lets the caller atomically clear
 * the cross-path flags when switching from path A → path B (avoids
 * a transient cross-path BadRequest from the validator).
 */
export function useSetMakerFlag() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      flag,
      value,
      clearOthers
    }: {
      id: string;
      flag: MakerFlag;
      value: boolean;
      clearOthers?: MakerFlag[];
    }) => {
      const dto: Partial<Record<MakerFlag, boolean>> = { [flag]: value };
      if (clearOthers) {
        for (const f of clearOthers) dto[f] = false;
      }
      const { data } = await positionOpenerAxiosApi.patch<EntryRule>(
        `/rules/${id}`,
        dto
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Hide a pair from the opener page. The pair stays
 * `matchedBy: 'manual'` and visible everywhere else (Opportunities,
 * Positions, Unwinding); only the opener filters it out of
 * `findManualConfirmed`. The PairsSyncService change-stream tick on
 * the backend then drops the Kalshi + Polymarket WS subscription via
 * the existing diff path. Optimistically removes the pair from the
 * cached `manual-confirmed-pairs` list so the row disappears the
 * moment the operator confirms — server invalidation lands the
 * canonical state right after.
 */
export function useHidePairFromOpener() {
  const qc = useQueryClient();
  return useMutation(
    async (pairId: string) => {
      const { data } = await predictionManagerAxiosApi.patch(
        `/pairs/${pairId}/hide-from-opener`
      );
      return data;
    },
    {
      onMutate: async (pairId) => {
        await qc.cancelQueries(['manual-confirmed-pairs', 'active']);
        const prev = qc.getQueryData<ManualConfirmedPair[]>([
          'manual-confirmed-pairs',
          'active'
        ]);
        if (Array.isArray(prev)) {
          qc.setQueryData<ManualConfirmedPair[]>(
            ['manual-confirmed-pairs', 'active'],
            prev.filter((p) => p._id !== pairId)
          );
        }
        return { prev };
      },
      onError: (_err, _pairId, ctx) => {
        // Rollback the optimistic removal on failure so the row
        // pops back rather than silently disappearing forever.
        if (ctx?.prev) {
          qc.setQueryData(['manual-confirmed-pairs', 'active'], ctx.prev);
        }
      },
      onSettled: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

export function useActivateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await positionOpenerAxiosApi.post<EntryRule>(
        `/rules/${id}/activate`
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function usePauseRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await positionOpenerAxiosApi.post<EntryRule>(
        `/rules/${id}/pause`
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useEntryRuleExecutions(ruleId: string | null) {
  return useQuery<EntryExecution[]>(
    ['entry-executions', ruleId],
    async () => {
      if (!ruleId) return [];
      const { data } = await positionOpenerAxiosApi.get<EntryExecution[]>(
        `/rules/${ruleId}/executions`
      );
      return data;
    },
    {
      enabled: !!ruleId,
      refetchInterval: 10000,
      keepPreviousData: true
    }
  );
}

export function useEntryOrders() {
  return useQuery<EntryOrdersResponse>(
    'entry-orders',
    async () => {
      const { data } = await positionOpenerAxiosApi.get<EntryOrdersResponse>(
        '/orders'
      );
      return data;
    },
    { refetchInterval: 3000, keepPreviousData: true }
  );
}

/**
 * Pair-manager response shape: each pair has `siteA` / `siteB` legs,
 * each tagged with a `platform`. siteA is *usually* Polymarket but the
 * adapter below treats platform as the source of truth so a future
 * matcher flip (Kalshi-first) doesn't silently misalign the legs.
 */
interface PairManagerSite {
  platform: 'polymarket' | 'kalshi';
  marketId?: string;
  title?: string;
  marketUrl?: string;
  yesTokenId?: string;
  noTokenId?: string;
  canonicalKey?: string;
}
interface PairManagerPair {
  _id: string;
  name?: string;
  pairName?: string;
  status?: string;
  archived?: boolean;
  expiryDate?: string | null;
  category?: string | null;
  siteA?: PairManagerSite;
  siteB?: PairManagerSite;
}

function adaptPair(raw: PairManagerPair): ManualConfirmedPair {
  const sites = [raw.siteA, raw.siteB].filter(
    (s): s is PairManagerSite => !!s,
  );
  const polySite = sites.find(s => s.platform === 'polymarket');
  const kalshiSite = sites.find(s => s.platform === 'kalshi');
  return {
    _id: raw._id,
    pairName: raw.pairName ?? raw.name ?? '',
    polymarketTitle: polySite?.title,
    kalshiTitle: kalshiSite?.title,
    expiryDate: raw.expiryDate ?? null,
    category: raw.category ?? null,
    status: raw.status,
    archived: raw.archived,
    polymarket: polySite
      ? {
          tokenId: polySite.yesTokenId ?? polySite.noTokenId,
          conditionId: polySite.marketId,
          marketUrl: polySite.marketUrl,
        }
      : null,
    kalshi: kalshiSite
      ? {
          ticker: kalshiSite.marketId,
          marketUrl: kalshiSite.marketUrl,
        }
      : null,
  };
}

/**
 * PANIC button — operator-initiated emergency stop on the opener:
 * batch-cancel every active maker buy and pause every entry rule. The
 * resulting cancellation events are surfaced as desired operator
 * cancellations (severity = warning, not error) so the in-app feed +
 * Slack distinguish them from engine faults.
 */
export function useEntryPanic() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await positionOpenerAxiosApi.post('/rules/panic');
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      },
    },
  );
}

export function useEntryResume() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await positionOpenerAxiosApi.post('/rules/resume');
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      },
    },
  );
}

/**
 * Stop/Start the position-opener deployment via prediction-manager. Mirrors
 * the unwinder controls: stop runs a precondition check + (optional) panic
 * + scale-to-0; start scales to 1 and waits for Ready. The state hook polls
 * every 3 s so the button flips promptly once the patch lands.
 */
export function useOpenerState() {
  return useQuery<OpenerState>(['opener-state'], getOpenerState, {
    refetchInterval: 3_000,
    keepPreviousData: true,
  });
}

export function useStopOpener() {
  const qc = useQueryClient();
  return useMutation(stopOpener, {
    onSuccess: job => {
      // Seed system/job so UpdatesProvider's stepper picks it up immediately.
      qc.setQueryData('system/job', job);
      qc.invalidateQueries('system/job');
      qc.invalidateQueries('entry-rules');
      qc.invalidateQueries(['opener-state']);
    },
  });
}

export function useStartOpener() {
  const qc = useQueryClient();
  return useMutation(startOpener, {
    onSuccess: job => {
      qc.setQueryData('system/job', job);
      qc.invalidateQueries('system/job');
      qc.invalidateQueries(['opener-state']);
    },
  });
}

/**
 * Manually-confirmed prediction pairs that are eligible for entry rules.
 * Lives on the pair-manager service (NOT position-opener) — re-uses the
 * existing `predictionManagerAxiosApi`. Refetches every 30 s so newly
 * confirmed pairs show up without a hard reload.
 *
 * The pair-manager returns the raw matcher schema (`siteA`/`siteB`/`name`);
 * `adaptPair` flattens that into the frontend's `ManualConfirmedPair`
 * interface so the row component doesn't need to know about the storage
 * shape.
 */
export function useManualConfirmedPairs() {
  return useQuery<ManualConfirmedPair[]>(
    ['manual-confirmed-pairs', 'active'],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairManagerPair[]>(
        '/pairs/manual-confirmed',
        { params: { status: 'active' } },
      );
      return Array.isArray(data) ? data.map(adaptPair) : [];
    },
    { refetchInterval: 30_000, keepPreviousData: true }
  );
}
